import React, { useEffect, useState } from 'react';
import Rodal from '../../../components/Rodal/Rodal';
import { Input, ModalBody, ModalHeader } from 'reactstrap';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import SortingOption, {
    SortOrder,
} from '../../../components/Table/models/bootstrap-table-sorting-option';
import { sortingFunction } from '../../../components/Table/models/table-helper';
import moment from 'moment';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import FileUploadsList from '../../../components/FileUploadsList/FileUploadsList';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import AnswerFile from '../../../models/DataRequestHub/AnswerFile';
import UserDetailsModal from '../../Admin/Permissions/Users/Modal/UserDetailsModal';
import { SeeMoreText } from '../../../components/SeeMoreText/SeeMoreText';
import { DataRoomUploadWarning } from '../DataRoomUploadWarning/DataRoomUploadWarning';

interface DataTableCellUploadPopupProps {
    projectId: number;
    tableId: number;
    columnId: number;
    rowId: number;
    isReadonlyMode: boolean;
    storageFolder?: string;
    isAdminView: boolean;
    close(): void;
    onSuccessfulUpload(): void;
    title?: string;
}

interface TableSortingState {
    data: any[];
    sortField: string;
    sortOrder: SortOrder;
}

const cellBaseUrl = (projectId: number, tableId: number, columnId: number, rowId: number) =>
    `/api/DataRequestProjects/${projectId}/dataTables/${tableId}/cells/${columnId}/${rowId}/files`;

export const DataTableCellUploadPopup = (props: DataTableCellUploadPopupProps) => {
    const axios = useStateSelector((s) => s.core.axios);
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState<AnswerFile[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>();
    const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
    const [uploadComment, setUploadComment] = useState('');
    const [isBulkDownloading, setIsBulkDownloading] = useState(false);
    const [isUserDetailsModalVisible, setIsUserDetailsModalVisible] =
        useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number>();

    const defaultSorted: SortingOption = { dataField: 'dateCreated', order: 'desc' };
    const [sortingOption, setSortingOption] = useState<SortingOption>(defaultSorted);

    const isAllSelected = tableData.length > 0 && selectedFileIds.length === tableData.length;
    const isIndeterminate = selectedFileIds.length > 0 && selectedFileIds.length < tableData.length;

    const baseUrl = cellBaseUrl(props.projectId, props.tableId, props.columnId, props.rowId);

    useEffect(() => {
        axios.get(baseUrl).then((response: any) => {
            if (response.status === 200) {
                sortFunction(response.data ?? [], defaultSorted);
            } else {
                createNotification('An error occurred while fetching files.', 'error');
            }
        });
    }, []);

    const handleSelectAll = (isSelect: boolean) => {
        setSelectedFileIds(isSelect ? tableData.map((f) => f.id) : []);
    };

    const handleOnSelect = (row: AnswerFile, isSelect: boolean) => {
        if (isSelect) {
            setSelectedFileIds((prev) =>
                prev.includes(row.id) ? prev : [...prev, row.id]
            );
        } else {
            setSelectedFileIds((prev) => prev.filter((id) => id !== row.id));
        }
    };

    const openUserDetails = (userId: number) => {
        setSelectedUserId(userId);
        setIsUserDetailsModalVisible(true);
    };

    const getColumns = () => [
        { dataField: 'id', text: '', hidden: true },
        {
            dataField: 'name',
            text: 'File',
            sort: true,
            formatter: (cellContent: string, row: AnswerFile) => (
                <div className="link-text" onClick={() => openFile(row.id)}>
                    {cellContent}
                </div>
            ),
        },
        {
            dataField: 'dateCreated',
            text: 'Upload Date',
            sort: true,
            style: { width: '120px' },
            formatter: (cellContent: Date) => moment(cellContent).format('L'),
        },
        {
            dataField: 'creatorName',
            text: 'Uploaded By',
            sort: true,
            formatter: (cellContent: string, row: AnswerFile) => {
                if (!cellContent) {
                    return '';
                }

                const roleSuffix = row.creatorRole
                    ? ` (${row.creatorRole})`
                    : '';

                if (
                    props.isAdminView &&
                    row.creatorUserId &&
                    !row.creatorIsAdmin
                ) {
                    return (
                        <>
                            <span
                                className="link-text"
                                onClick={() =>
                                    openUserDetails(row.creatorUserId)
                                }>
                                {cellContent}
                            </span>
                            {roleSuffix}
                        </>
                    );
                }

                return `${cellContent}${roleSuffix}`;
            },
        },
        {
            dataField: 'comment',
            text: 'Comment',
            sort: false,
            style: {
                width: '200px',
            },
            formatter: (cellContent: string) => {
                if (!cellContent) {
                    return '';
                }

                return <SeeMoreText text={cellContent} />;
            },
        },
    ];

    const selectRow = {
        mode: 'checkbox' as const,
        clickToSelect: false,
        selected: selectedFileIds,
        onSelect: handleOnSelect,
        onSelectAll: handleSelectAll,
        selectionHeaderRenderer: ({ indeterminate, checked }: { indeterminate: boolean; checked: boolean }) => (
            <input
                type="checkbox"
                checked={checked || isAllSelected}
                ref={(input) => {
                    if (input) input.indeterminate = indeterminate || isIndeterminate;
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
            />
        ),
        selectionRenderer: ({ checked, rowIndex }: { checked: boolean; rowIndex: number }) => {
            const row = tableData[rowIndex];
            if (!row) return null;
            const isRowSelected = selectedFileIds.includes(row.id);
            return (
                <input
                    type="checkbox"
                    checked={isRowSelected}
                    onChange={() => handleOnSelect(row, !isRowSelected)}
                />
            );
        },
    };

    const handleTableChange = (_props: any, state: TableSortingState) => {
        const newSortingOption: SortingOption = {
            dataField: state.sortField,
            order: state.sortOrder as SortOrder,
        };
        setSortingOption(newSortingOption);
        sortFunction(state.data, newSortingOption);
    };

    const sortFunction = (data: any[], option: SortingOption) => {
        setTableData(sortingFunction([...data], option));
    };

    const deleteFile = (file: File) => {
        if (!isLoading) {
            const index = selectedFiles.indexOf(file);
            if (index > -1) {
                selectedFiles.splice(index, 1);
                setSelectedFiles([...selectedFiles]);
            }
        }
    };

    const uploadToTemp = async (files: File[]) => {
        const requests = files.map((f) => {
            const formData = new FormData();
            formData.append('File', f);
            return axios.post(`/api/temporaryfiles`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        });
        const responses = await Promise.all(requests);
        if (responses.some((r) => r.status !== 200)) {
            createNotification('Some files are not uploaded due to an unexpected error.', 'error');
            setIsLoading(false);
            throw new Error('Temporary file upload failed');
        }
        return responses.map((r) => r.data as AnswerFile);
    };

    const saveUploads = async () => {
        setIsLoading(true);
        const tempFiles = await uploadToTemp(selectedFiles);
        const fileIds = tempFiles.map((f) => f.id);

        axios
            .post(baseUrl, {
                fileIds,
                comment: uploadComment.trim() || undefined,
            })
            .then((response: any) => {
                if (response.status === 200) {
                    props.onSuccessfulUpload();
                    setSelectedFiles([]);
                    setUploadComment('');
                    axios.get(baseUrl).then((r: any) => {
                        if (r.status === 200) sortFunction(r.data ?? [], sortingOption);
                    });
                } else {
                    createNotification('An error occurred while saving files.', 'error');
                }
            })
            .finally(() => setIsLoading(false));
    };

    const openFile = (fileId: number) => {
        axios.get(`${baseUrl}/${fileId}`).then((response: any) => {
            window.open(response.data, '_blank');
        });
    };

    const bulkDownloadFiles = async () => {
        if (selectedFileIds.length === 0) return;
        setIsBulkDownloading(true);
        try {
            const response = await axios.post(
                `${baseUrl}/bulk-download`,
                { fileIds: selectedFileIds },
                { responseType: 'blob' }
            );
            if (response.status === 200) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const singleFile =
                    selectedFileIds.length === 1
                        ? tableData.find((f) => f.id === selectedFileIds[0])
                        : undefined;
                link.setAttribute('download', singleFile ? singleFile.name : 'files.zip');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                setSelectedFileIds([]);
            } else {
                createNotification('An error occurred while downloading files.', 'error');
            }
        } catch {
            createNotification('An error occurred while downloading files.', 'error');
        } finally {
            setIsBulkDownloading(false);
        }
    };

    const handleFileSelection = (fileList: FileList) => {
        const files = Array.from(fileList);
        setSelectedFiles(selectedFiles?.concat(files) ?? files);
    };

    const addMoreDocuments = () => document.getElementById('dtCellFileInput').click();

    return (
        <div className="documents-upload-popup">
            <Rodal
                visible={true}
                onClose={() => {
                    if (!isLoading) props.close();
                }}
                width={600}>
                <ModalHeader>{props.title ?? 'Cell Files'}</ModalHeader>
                <ModalBody>
                    <DataRoomUploadWarning
                        projectId={props.projectId}
                        storageFolder={props.storageFolder}
                        isAdminView={props.isAdminView}
                        isReadonlyMode={props.isReadonlyMode}
                    />
                    <div className="uploaded-documents-section">
                        <div className="section-header">
                            <label className="section-title">Uploaded Documents</label>
                            <ButtonLoader
                                buttonText="Download Selected"
                                loaderButtonText=""
                                disabled={selectedFileIds.length === 0 || isBulkDownloading}
                                isLoading={isBulkDownloading}
                                onClick={bulkDownloadFiles}
                                className="btn btn-primary btn-sm"
                            />
                        </div>
                        <div className="document-uploads-table">
                            <BootstrapTable
                                id="dt-cell-uploads-table"
                                striped
                                bootstrap4
                                remote
                                keyField="id"
                                data={tableData}
                                columns={getColumns()}
                                defaultSorted={[defaultSorted]}
                                onTableChange={handleTableChange}
                                selectRow={selectRow}
                                noDataIndication="No Data available"
                            />
                        </div>
                    </div>
                    {!props.isReadonlyMode && (
                        <div className="new-documents-section">
                            <label className="section-title">New Uploads</label>
                            <input
                                hidden
                                type="file"
                                id="dtCellFileInput"
                                onChange={(e) => {
                                    handleFileSelection(e.target.files);
                                    e.target.value = null;
                                }}
                                multiple
                            />
                            <div className="uploads">
                                {selectedFiles ? (
                                    <FileUploadsList fileList={selectedFiles} onDeleteClick={deleteFile} />
                                ) : (
                                    []
                                )}
                            </div>
                            <div className="upload-comment-field">
                                <label className="section-title">Comment</label>
                                <Input
                                    type="textarea"
                                    value={uploadComment}
                                    disabled={isLoading || props.isReadonlyMode}
                                    placeholder="Add a comment for this upload (optional)"
                                    onChange={(e) =>
                                        setUploadComment(e.target.value)
                                    }
                                />
                            </div>
                            <button
                                className="btn btn-primary"
                                disabled={isLoading || props.isReadonlyMode}
                                onClick={addMoreDocuments}>
                                Add More Documents
                            </button>
                        </div>
                    )}
                    <div className="buttons-wrapper">
                        <button
                            disabled={isLoading}
                            className="btn btn-cancel"
                            onClick={() => {
                                if (!isLoading) props.close();
                            }}>
                            Cancel
                        </button>
                        {!props.isReadonlyMode && (
                            <ButtonLoader
                                buttonText="Save"
                                loaderButtonText=""
                                disabled={isLoading || !selectedFiles?.length || props.isReadonlyMode}
                                isLoading={isLoading}
                                onClick={saveUploads}
                                className="btn btn-primary"
                            />
                        )}
                    </div>
                </ModalBody>
            </Rodal>
            {isUserDetailsModalVisible && (
                <UserDetailsModal
                    isVisible={isUserDetailsModalVisible}
                    userID={selectedUserId}
                    onClose={() => setIsUserDetailsModalVisible(false)}
                />
            )}
        </div>
    );
};
